import React, { useEffect, useRef } from 'react';
import { type ColorType, createChart, LineSeries, type IChartApi, type UTCTimestamp } from 'lightweight-charts';
import type { PostTradeBalances } from '@einhaus/project314-types';
import { normalizePerformanceChartData } from './normalizePerformanceChartData';

interface PerformanceChartProps {
    balances: PostTradeBalances[];
    height?: number;
    width?: string;
    lineColor?: string;
    comparisonBalances?: PostTradeBalances[];
}

const PerformanceChart: React.FC<PerformanceChartProps> = ({
    balances,
    comparisonBalances,
    height = 300,
    width = '100%',
    lineColor = '#2962FF'
}) => {
    const chartContainerRef = useRef<HTMLDivElement>(null);
    const chartRef = useRef<IChartApi | null>(null);

    useEffect(() => {
        if (!chartContainerRef.current) return;

        // Clear previous chart
        if (chartRef.current) {
            chartRef.current.remove();
            chartRef.current = null;
        }

        const normalizedData = normalizePerformanceChartData(balances);

        if (normalizedData.points.length === 0) return;

        // Create chart
        const chartOptions = {
            layout: {
                textColor: 'white',
                background: { type: 'solid' as ColorType.Solid, color: 'black' }
            },
            height,
            width: width === '100%' ? undefined : parseInt(width, 10)
        };

        chartRef.current = createChart(chartContainerRef.current, chartOptions);
        const lineSeries = chartRef.current.addSeries(LineSeries, { color: lineColor });

        const data = normalizedData.points.map((balance) => ({
            value: balance.balance,
            time: balance.timestamp as UTCTimestamp
        }));

        lineSeries.setData(data);
        if (comparisonBalances) {
            const comparisonSeries = chartRef.current.addSeries(LineSeries, { color: '#22c55e' });
            comparisonSeries.setData(
                normalizePerformanceChartData(comparisonBalances).points.map((point) => ({
                    value: point.balance,
                    time: point.timestamp as UTCTimestamp
                }))
            );
        }
        chartRef.current.timeScale().fitContent();

        // Cleanup function
        return () => {
            if (chartRef.current) {
                chartRef.current.remove();
                chartRef.current = null;
            }
        };
    }, [balances, comparisonBalances, height, width, lineColor]);

    return <div ref={chartContainerRef} style={{ minHeight: `${height}px`, width }} />;
};

export default PerformanceChart;
